/**
 * Tileable pattern texture generators for Deck.gl FillStyleExtension layers.
 *
 * All canvases are 512x512 by default. For seamless tiling, pattern period
 * MUST divide the canvas size cleanly (use 32 / 64 / 128 / 256, not 48 / 80).
 *
 * Mask-mode patterns are drawn in solid white on transparent so that the
 * Deck layer's getFillColor recolors them per-feature. Image-mode patterns
 * draw their own RGBA and are used as-is.
 *
 * See agent wiki tech/mapbox.md for the why behind the analytic stripe
 * approach (TL;DR: ctx.rotate does NOT produce a seamlessly-tileable image).
 */

const DEFAULT_SIZE = 512;

/**
 * Solid dots on a grid. Tileable when spacing divides size.
 */
export function makeDotsCanvas({size = DEFAULT_SIZE, spacing = 128, radius = 28, color = 'rgba(255,255,255,1)'} = {}) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = color;
  for (let x = spacing / 2; x < size; x += spacing) {
    for (let y = spacing / 2; y < size; y += spacing) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  return c;
}

/**
 * Diagonal stripes (45° forward "/" or 45° backward "\\"), generated analytically
 * per pixel for seamless tiling whenever `period` divides `size`.
 *
 * Per-pixel formula:
 *   '/'  → distance to nearest line where (x + y) ≡ 0 (mod period)
 *   '\\' → distance to nearest line where (x - y) ≡ 0 (mod period)
 * Then a 1px alpha feather around the half-thickness boundary for antialiasing.
 *
 * @param {object} opts
 * @param {string} opts.direction 'forward' or 'backward'
 * @param {number} opts.period   stripe spacing in source pixels (must divide size)
 * @param {number} opts.thickness perpendicular line thickness in source pixels
 * @param {number} [opts.size=512]
 * @param {number} [opts.feather=1] alpha-feather width in source pixels
 */
export function makeDiagonalStripeCanvas({direction = 'forward', period = 64, thickness = 14, size = DEFAULT_SIZE, feather = 1} = {}) {
  if (size % period !== 0) {
    console.warn(`[PsDeckMaps] stripe period ${period} does not divide size ${size} — will show seams`);
  }
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  const img = ctx.createImageData(size, size);
  const data = img.data;
  const half = thickness / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const u = (direction === 'forward') ? (x + y) : (x - y);
      const m = ((u % period) + period) % period;
      const d = Math.min(m, period - m);
      // perpendicular distance (line is at 45°); divide by sqrt(2)
      const perpD = d / Math.SQRT2;
      let alpha;
      if (perpD <= half - feather)      alpha = 255;
      else if (perpD >= half)           alpha = 0;
      else                              alpha = Math.round(255 * (half - perpD) / feather);
      const i = (y * size + x) * 4;
      data[i] = 255; data[i + 1] = 255; data[i + 2] = 255; data[i + 3] = alpha;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

/**
 * Crosshatch — both diagonals overlaid. Use period that divides size.
 */
export function makeCrosshatchCanvas({period = 64, thickness = 10, size = DEFAULT_SIZE} = {}) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  ctx.strokeStyle = 'rgba(255,255,255,1)';
  ctx.lineWidth = thickness;
  ctx.lineCap = 'butt';
  // forward
  for (let x = -size; x <= size * 2; x += period) {
    ctx.beginPath();
    ctx.moveTo(x, size); ctx.lineTo(x + size, 0); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, 0); ctx.lineTo(x + size, size); ctx.stroke();
  }
  return c;
}

/**
 * Vertical stripes. Period must divide size.
 */
export function makeVerticalStripesCanvas({period = 64, thickness = 16, size = DEFAULT_SIZE} = {}) {
  const c = document.createElement('canvas');
  c.width = size; c.height = size;
  const ctx = c.getContext('2d');
  ctx.fillStyle = 'rgba(255,255,255,1)';
  for (let x = (period - thickness) / 2; x < size; x += period) {
    ctx.fillRect(x, 0, thickness, size);
  }
  return c;
}

/**
 * Upload a canvas as a luma.gl Texture with mipmaps + REPEAT wrap.
 * Required for the no-mod shader fix to render cleanly at parcel zoom.
 *
 * @param {Device} device  luma.gl device (from Deck.device or overlay._deck.device)
 * @param {HTMLCanvasElement|HTMLImageElement} source
 * @returns {Texture}
 */
export function makeMippedTexture(device, source) {
  const w = source.width || source.naturalWidth;
  const h = source.height || source.naturalHeight;
  const mipLevels = device.getMipLevelCount(w, h);
  const tex = device.createTexture({
    data: source,
    width: w,
    height: h,
    mipLevels,
    sampler: {
      minFilter: 'linear',
      magFilter: 'linear',
      mipmapFilter: 'linear',
      addressModeU: 'repeat',
      addressModeV: 'repeat',
      lodMinClamp: 0,
      lodMaxClamp: 32
    }
  });
  if (device.type === 'webgl' && tex.generateMipmapsWebGL) {
    tex.generateMipmapsWebGL();
  }
  return tex;
}

/**
 * Convenience: name → canvas factory for common patterns.
 */
export const PATTERN_PRESETS = {
  dots:        () => makeDotsCanvas(),
  dense_dots:  () => makeDotsCanvas({spacing: 64, radius: 22}),
  hatch:       () => makeDiagonalStripeCanvas({direction: 'forward'}),
  hatch_back:  () => makeDiagonalStripeCanvas({direction: 'backward'}),
  cross:       () => makeCrosshatchCanvas(),
  vstripe:     () => makeVerticalStripesCanvas()
};
